import { useState, useMemo, forwardRef } from 'react';
import { useField, useFormikContext } from 'formik';
import DatePicker from 'react-datepicker';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { ErrorDisplay } from '@/components/formComponents/';
import { FiCalendar } from 'react-icons/fi';
import { MIN_DRIVER_AGE, MAX_DRIVER_AGE } from '@/constants/driverAge';
import { ukNow } from '@/utils/date/ukFrame';
import 'react-datepicker/dist/react-datepicker.css';

interface DateOfBirthProps {
    name: string;
}

interface CalendarTriggerProps {
    onClick?: () => void;
}

const MONTHS = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
];

// UK wall clock: age eligibility is judged at the UK depot, so "today" must
// not drift a day either side on non-UK browsers
const currentYear = ukNow().getFullYear();

const minDate = new Date(currentYear - 100, 0, 1);
const maxDate = ukNow();

const YEARS = Array.from({ length: 100 }, (_, i) => currentYear - i);

const getDaysInMonth = (month: string, year: string): number => {
    if (!month) return 31;
    return new Date(Number(year) || 2000, Number(month), 0).getDate();
};

const calculateAge = (day: string, month: string, year: string): number => {
    const today = ukNow();
    const birthDate = new Date(Number(year), Number(month) - 1, Number(day));
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
        age--;
    }
    return age;
};

// Custom trigger — calendar icon button
const CalendarTrigger = forwardRef<HTMLButtonElement, CalendarTriggerProps>(
    ({ onClick }, ref) => (
        <button
            ref={ref}
            type="button"
            onClick={onClick}
            aria-label="Open date picker"
            className={clsx(
                'flex items-center justify-center gap-2',
                'min-h-[44px] w-32 px-3',
                'bg-white border-l border-[#E5E5E5]',
                'text-gray-500 hover:text-gray-800',
                'transition-colors duration-150',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-blue-500'
            )}
        >
            <FiCalendar size={18} aria-hidden="true" />
            <span className="text-xs whitespace-nowrap">Pick a date</span>
        </button>
    )
);

CalendarTrigger.displayName = 'CalendarTrigger';

const DateOfBirth = ({ name }: DateOfBirthProps) => {
    const [day, setDay] = useState('');
    const [month, setMonth] = useState('');
    const [year, setYear] = useState('');
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);

    const [, meta] = useField(name);
    const { setFieldValue, setFieldError, setFieldTouched } =
        useFormikContext();

    const isInErrorState = meta.touched && !!meta.error;

    const daysInMonth = useMemo(
        () => getDaysInMonth(month, year),
        [month, year]
    );

    const days = Array.from({ length: daysInMonth }, (_, i) =>
        String(i + 1).padStart(2, '0')
    );

    const handleChange = (
        newDay: string,
        newMonth: string,
        newYear: string
    ) => {
        const maxDays = getDaysInMonth(newMonth, newYear);
        const safeDay = newDay && Number(newDay) > maxDays ? '' : newDay;

        if (safeDay !== day) setDay(safeDay);

        setFieldValue('dobDay', safeDay);
        setFieldValue('dobMonth', newMonth);
        setFieldValue('dobYear', newYear);

        if (safeDay && newMonth && newYear) {
            // Sync datepicker to match dropdown state
            setSelectedDate(
                new Date(Number(newYear), Number(newMonth) - 1, Number(safeDay))
            );

            const dob = `${safeDay}/${newMonth}/${newYear}`;
            setFieldValue(name, dob);
            setFieldTouched(name, true, false);

            const age = calculateAge(safeDay, newMonth, newYear);

            if (age < MIN_DRIVER_AGE || age > MAX_DRIVER_AGE) {
                setFieldError(
                    name,
                    `Driver must be between ${MIN_DRIVER_AGE} and ${MAX_DRIVER_AGE} years old`
                );
                toast.error(
                    (t) => (
                        <div className="flex items-start gap-3">
                            <span className="text-sm leading-snug">
                                We can only insure drivers between the ages of
                                25 and 75.
                            </span>
                            <button
                                type="button"
                                onClick={() => toast.dismiss(t.id)}
                                aria-label="Dismiss notification"
                                className={clsx(
                                    'shrink-0 text-xs font-semibold uppercase tracking-wide',
                                    'text-white/80 hover:text-white transition-colors',
                                    'focus:outline-none focus-visible:underline'
                                )}
                            >
                                Close
                            </button>
                        </div>
                    ),
                    { id: 'dob-age-error', duration: 6000 }
                );
            } else {
                setFieldError(name, undefined);
            }
        } else {
            setSelectedDate(null);
            setFieldValue(name, '');
        }
    };

    const handleDatePickerChange = (date: Date | null) => {
        if (!date) return;
        setSelectedDate(date);

        const newDay = String(date.getDate()).padStart(2, '0');
        const newMonth = String(date.getMonth() + 1).padStart(2, '0');
        const newYear = String(date.getFullYear());

        setDay(newDay);
        setMonth(newMonth);
        setYear(newYear);
        handleChange(newDay, newMonth, newYear);
    };

    const selectClasses = clsx(
        'flex-1 min-h-[44px] p-2 bg-white text-sm outline-none cursor-pointer',
        'border-r border-[#E5E5E5]',
        'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500'
    );

    const containerClasses = clsx(
        'flex w-full',
        isInErrorState && 'border-2 border-red-600'
    );

    return (
        <div className="w-full">
            <div className={containerClasses}>
                <label htmlFor="dobDay" className="sr-only">
                    Day of birth
                </label>
                <select
                    id="dobDay"
                    name="dobDay"
                    value={day}
                    onChange={(e) => {
                        setDay(e.target.value);
                        handleChange(e.target.value, month, year);
                    }}
                    className={selectClasses}
                >
                    <option value="">Day</option>
                    {days.map((d) => (
                        <option key={d} value={d}>
                            {d}
                        </option>
                    ))}
                </select>

                <label htmlFor="dobMonth" className="sr-only">
                    Month of birth
                </label>
                <select
                    id="dobMonth"
                    name="dobMonth"
                    value={month}
                    onChange={(e) => {
                        setMonth(e.target.value);
                        handleChange(day, e.target.value, year);
                    }}
                    className={selectClasses}
                >
                    <option value="">Month</option>
                    {MONTHS.map((m) => (
                        <option key={m.value} value={m.value}>
                            {m.label}
                        </option>
                    ))}
                </select>

                <label htmlFor="dobYear" className="sr-only">
                    Year of birth
                </label>
                <select
                    id="dobYear"
                    name="dobYear"
                    value={year}
                    onChange={(e) => {
                        setYear(e.target.value);
                        handleChange(day, month, e.target.value);
                    }}
                    className={selectClasses}
                >
                    <option value="">Year</option>
                    {YEARS.map((y) => (
                        <option key={y} value={String(y)}>
                            {y}
                        </option>
                    ))}
                </select>

                <DatePicker
                    selected={selectedDate}
                    onChange={handleDatePickerChange}
                    customInput={<CalendarTrigger />}
                    minDate={minDate}
                    maxDate={maxDate}
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    className="border-b border-[#E5E5E5]"
                    popperPlacement="bottom-end"
                    aria-label="Date of birth calendar picker"
                />
            </div>
            <ErrorDisplay meta={meta} />
        </div>
    );
};

export default DateOfBirth;
